import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { LayersIcon, PlusIcon, TrashIcon, UpdateIcon } from '@radix-ui/react-icons'
import ImportPsdDialog from 'Editor/PsdImport/ImportPsdDialog'
import CptUploadProgressBar from 'Editor/BottomPanel/BatchUpload/CptUploadProgressBar'
import { ArrowUpDown } from 'lucide-react'
import { postFetcher } from 'api/swr'
import { mutate } from 'swr'
import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import useSWRMutation from 'swr/mutation'
import { toast } from '@/components/ui/use-toast'
import { useUI } from 'Editor'
import { softDeleteLayer } from './layerDelete'

const DataTableToolbar = ({
  layers,
  sortBy,
  onSortByChange,
  zDisplayReversed,
  onToggleZDisplay,
  selectedIds,
  selectedLayers,
  onClearSelection,
}) => {
  const selectedCount = selectedIds?.size || 0
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-4">
        <div className="flex items-center text-sm text-muted-foreground">
          <LayersIcon className="mr-2 h-4 w-4" /> {layers?.length}
        </div>
        <RadioGroup
          value={sortBy}
          onValueChange={onSortByChange}
          className="flex flex-row gap-4"
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem value="playIndex" id="layer-sort-play" />
            <Label htmlFor="layer-sort-play" className="font-normal">
              游玩顺序
            </Label>
          </div>
          <div className="flex items-center gap-1">
            <RadioGroupItem value="z" id="layer-sort-z" />
            <Label htmlFor="layer-sort-z" className="font-normal">
              叠层顺序
            </Label>
            {sortBy === 'z' ? (
              <Button
                type="button"
                variant={zDisplayReversed ? 'secondary' : 'ghost'}
                size="icon"
                className="h-7 w-7"
                title={zDisplayReversed ? '恢复默认叠层展示' : '倒序展示叠层'}
                onClick={onToggleZDisplay}
              >
                <ArrowUpDown className="h-4 w-4" />
              </Button>
            ) : null}
          </div>
        </RadioGroup>
        {selectedCount > 0 ? (
          <BulkDeleteLayers
            selectedLayers={selectedLayers}
            onCleared={onClearSelection}
          />
        ) : null}
      </div>
      <div className="ml-auto flex min-w-0 items-center gap-2">
        <CptUploadProgressBar />
        <ImportPsdDialog />
        <CreateButton />
      </div>
    </div>
  )
}

const BulkDeleteLayers = ({ selectedLayers, onCleared }) => {
  const { tid } = useParams()
  const lid = useUI((state) => state.lid)
  const toggleLayer = useUI((state) => state.toggleLayer)
  const [open, setOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const count = selectedLayers.length

  const handleDelete = async () => {
    if (deleting || count === 0) return
    setDeleting(true)
    try {
      for (const layer of selectedLayers) {
        await softDeleteLayer(tid, layer.id)
      }
      await mutate(['/layers', { tid }])
      if (selectedLayers.some((layer) => layer.id === lid)) {
        toggleLayer('draft')
      }
      onCleared?.()
      setOpen(false)
      toast({
        title: 'Layer Deleted',
        description:
          count === 1
            ? 'The layer has been successfully deleted.'
            : `${count} layers have been successfully deleted.`,
        className: 'bg-green-500 text-white',
      })
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Delete failed',
        description: error instanceof Error ? error.message : String(error),
      })
    } finally {
      setDeleting(false)
    }
  }

  return (
    <>
      <Button
        variant="destructive"
        size="sm"
        onClick={() => setOpen(true)}
      >
        <TrashIcon className="mr-1 h-4 w-4" />
        删除 {count} 层
      </Button>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (deleting) return
          setOpen(next)
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete {count} layer{count > 1 ? 's' : ''}?</DialogTitle>
            <DialogDescription>
              和单独删除一样，软删除这些层及其组件。
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-40 overflow-auto text-sm">
            {selectedLayers.map((layer) => (
              <div key={layer.id}>{layer.name || layer.id}</div>
            ))}
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              disabled={deleting}
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleting}
              onClick={() => void handleDelete()}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

const CreateButton = () => {
  const { tid } = useParams()
  const { trigger, isMutating } = useSWRMutation('/layers', postFetcher)

  const handleClick = () => {
    trigger(
      { tid, name: 'new layer', cover: 'UWj' },
      { onSuccess: () => mutate(['/layers', { tid }]) },
    )
  }

  return (
    <Button
      variant="outline"
      disabled={isMutating}
      onClick={handleClick}
    >
      {isMutating ? (
        <>
          <UpdateIcon className="mr-2 h-4 w-4 animate-spin" /> Creating...
        </>
      ) : (
        <>
          <PlusIcon className="mr-2 h-4 w-4" /> Add layer
        </>
      )}
    </Button>
  )
}

export default DataTableToolbar
